import React from 'react';
import { Tag, Typography } from '@guildeducationinc/recess';
import {
	GetDeferredTargetTermQuery,
	useGetDeferredTargetTermQuery,
} from 'src/graphqlTypes/appSyncTypes';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import { DeferredPopover } from '@src/components/ProgramDetails/ProgramHighlightsPanel/DeferredPopover';
import { formatDate } from 'src/utilities/dateUtility';
import { Skeleton } from 'src/components/UserFacetSkeletonLoader';
import { useSearchContext } from '@src/components/Providers/SearchProvider';
import { StartApplicationButton } from '@src/components/ProgramDetails/StartApplicationButton';
import styles from './DeferredDateCta.module.scss';

interface DeferredDateCtaProps {
	program: GetMemberProgramQuery['memberProgram'];
}

export const DeferredDateCta: React.FC<DeferredDateCtaProps> = ({ program }) => {
	const { memberPlanData } = useSearchContext();
	const eligible = memberPlanData.eligibility.eligible;

	const { data, loading, error } = useGetDeferredTargetTermQuery({
		variables: { termId: program.deferredTargetTermUuid },
		context: { gqlServer: 'global' },
	});

	const getNextStartDate = (deferredData: GetDeferredTargetTermQuery) => {
		if (deferredData?.AcademicService_getTermById) {
			// startDate should come back in this format 2021-10-01T00:00:00.000Z
			const dateOnly = deferredData?.AcademicService_getTermById.startDate.slice(0, 10);
			const exclusiveAppDueDate = new Date(dateOnly);
			const inclusiveAppDueDate = exclusiveAppDueDate.setDate(exclusiveAppDueDate.getDate() + 1);

			return formatDate(new Date(inclusiveAppDueDate), {
				month: '2-digit',
				day: '2-digit',
				year: '2-digit',
			});
		}
	};

	return (
		<Skeleton className={styles.mainSkeleton} dataLoading={loading}>
			<div data-testid='date-cta'>
				<Typography className={styles.deferredMessage} testid='deadline-date' variant='label1'>
					Current start date is full
				</Typography>
				<Typography className={styles.deferredMessageDate} testid='start-date' variant='label1'>
					Next start date: {error ? 'Unable to retrieve next start date' : getNextStartDate(data)}
				</Typography>
				{eligible ? (
					<>
						<DeferredPopover />
						<div className={styles.deferredDateCtaButton}>
							<Skeleton className={styles.buttonSkeleton} dataLoading={loading}>
								<StartApplicationButton isDeferred program={program} />
							</Skeleton>
						</div>
					</>
				) : null}
				<div className={styles.tag}>
					<Tag appearance='quiet' color='yellow' testid='deadline-date-tag'>
						Early application
					</Tag>
				</div>
			</div>
		</Skeleton>
	);
};
